<template>
  <ModalVue
    :modalActive="modalActive"
    max-width="max-w-5xl"
    has-header-border
    has-footer-border
    @close="emit('close')"
  >
    <template #header>
      <p class="text-start text-xl font-bold">Localização da unidade</p>
      <p class="mt-0.5 text-start text-sm text-gray-500">{{ subtitle }}</p>
      <PinStepHeader class="mt-3" :step="step" />
    </template>

    <div class="rounded-xl bg-[#EEF1F3] p-3 md:p-4">
      <StepFind
        v-if="step === 1"
        :geo="geo"
        :registered-address="registeredAddress"
        @next="goNext"
      />

      <StepPosition
        v-else-if="step === 2"
        :geo="geo"
        :map-type="mapType"
        :doctor-points="doctor.points.value"
        :doctor-summary="doctor.summary.value"
        :is-loading-points="doctor.isLoading.value"
        @update:map-type="mapType = $event"
        @center-doctors="centerOnDoctors"
      />

      <StepConfirm
        v-else
        :geo="geo"
        :unit-name="unitName"
        :map-type="mapType"
        :doctor-summary="doctor.summary.value"
        :mode="mode"
      />
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center gap-2">
        <!--
          Só na edição existe ponto original para voltar; no cadastro um botão
          desabilitado sem explicação não ajudaria ninguém.
        -->
        <OutlinedButton
          v-if="step === 2 && geo.canResetLocation.value"
          text="Resetar localização"
          :prepend-icon="mdiRestore"
          width="auto"
          :height="MODAL_ACTION_HEIGHT"
          class="!w-auto !px-4 text-sm"
          @click="geo.resetLocation()"
          data-testid="pin-footer-reset-button"
        />

        <div class="ml-auto flex flex-wrap items-center gap-2">
          <OutlinedButton
            v-if="step === 1"
            text="Sair"
            width="140px"
            :height="MODAL_ACTION_HEIGHT"
            @click="emit('close')"
            data-testid="pin-exit-button"
          />
          <OutlinedButton
            v-else
            text="Voltar"
            width="140px"
            :height="MODAL_ACTION_HEIGHT"
            @click="goBack"
            data-testid="pin-back-button"
          />

          <FilledButton
            v-if="step < 3"
            text="Continuar"
            width="200px"
            :height="MODAL_ACTION_HEIGHT"
            :disabled="!canContinue"
            @click="goNext"
            data-testid="pin-continue-button"
          />
          <FilledButton
            v-else
            text="Salvar pino"
            width="200px"
            :height="MODAL_ACTION_HEIGHT"
            :disabled="!geo.canSave.value"
            :loading="isSaving"
            @click="save"
            data-testid="pin-save-button"
          />
        </div>
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { mdiRestore } from '@mdi/js'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { MODAL_ACTION_HEIGHT } from '../../constants'
import type { UnitGeolocationApi } from '../../composables/useUnitGeolocation'
import { useUnitDoctorPoints } from '../../composables/useUnitDoctorPoints'
import PinStepHeader from './PinStepHeader.vue'
import StepFind from './StepFind.vue'
import StepPosition from './StepPosition.vue'
import StepConfirm from './StepConfirm.vue'
import type { PinModalMode, PinStep } from './pinModalTypes'

const props = withDefaults(
  defineProps<{
    modalActive: boolean
    /** API do useUnitGeolocation criada pelo orquestrador (estado compartilhado). */
    geo: UnitGeolocationApi
    mode: PinModalMode
    unitName: string
    /** Endereço textual do cadastro, para o "Usar o endereço do cadastro" do passo 1. */
    registeredAddress: string
    healthUnitId: number | null
    /** A fila e a faixa de sugestão abrem direto no passo 2, com o ponto já semeado. */
    initialStep?: PinStep
    isSaving?: boolean
  }>(),
  { initialStep: 1, isSaving: false }
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'confirm', payload: ReturnType<UnitGeolocationApi['toConfirmPayload']>): void
}>()

const step = ref<PinStep>(props.initialStep)
const mapType = ref<'roadmap' | 'satellite'>('roadmap')

const doctor = useUnitDoctorPoints(toRef(props, 'healthUnitId'))

const SUBTITLES: Record<PinStep, string> = {
  1: 'Passo 1 de 3 — encontre o prédio da unidade pelo nome, como no Google Maps.',
  2: 'Passo 2 de 3 — posicione o marcador sobre o prédio e defina o raio.',
  3: 'Passo 3 de 3 — confira e salve.'
}

const subtitle = computed(() => SUBTITLES[step.value])

const canContinue = computed(() => {
  if (step.value === 1) return props.geo.foundLocation.value && !!props.geo.unitType.value
  return props.geo.canConfirm.value
})

function openAtInitialStep(): void {
  step.value = props.initialStep
  // O satélite é o que revela o prédio sob o marcador — o mapa comum esconde o erro.
  mapType.value = step.value >= 2 ? 'satellite' : 'roadmap'
  if (props.healthUnitId) doctor.load()
}

watch(
  () => props.modalActive,
  (isOpen) => isOpen && openAtInitialStep(),
  { immediate: true }
)

function goNext(): void {
  if (step.value === 3 || !canContinue.value) return
  step.value = (step.value + 1) as PinStep
  if (step.value === 2) mapType.value = 'satellite'
}

function goBack(): void {
  if (step.value === 1) return
  step.value = (step.value - 1) as PinStep
}

/** Sugestão da fila manda no alvo; sem ela, o centro da nuvem de checks. */
function centerOnDoctors(): void {
  if (props.geo.suggestedPoint.value) {
    props.geo.centerOnSuggestion()
    return
  }
  if (doctor.centroid.value) props.geo.updateCoordinates(doctor.centroid.value)
}

function save(): void {
  emit('confirm', props.geo.toConfirmPayload())
}
</script>
